import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { logWarn } from "@/lib/log";

export const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

export const isStopped = (e) => e?.name === "AbortError" || e?.name === "CanceledError" || e?.code === "ERR_CANCELED" || e?.response?.status === 499;

export const fmtCredits = (n) => Number(n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

export function stoppedToast(charged) {
  toast(charged ? `Stopped · ${fmtCredits(charged)} credit used` : "Stopped · no credits used");
}

// One Stop pattern for every AI action: each run gets a job id the server can cancel, plus a local abort.
export function useAiJob({ onStopped } = {}) {
  const job = useRef(null);
  const [running, setRunning] = useState(false);
  useEffect(() => () => job.current?.ctrl.abort(), []);
  const run = useCallback(async (fn) => {
    const j = { id: newId(), ctrl: new AbortController() };
    job.current = j;
    setRunning(true);
    try {
      return await fn({ headers: { "X-Job-Id": j.id }, signal: j.ctrl.signal });
    } finally {
      if (job.current === j) { job.current = null; setRunning(false); }
    }
  }, []);
  const stop = useCallback(() => {
    const j = job.current;
    if (!j) return;
    job.current = null;
    setRunning(false);
    j.ctrl.abort();
    http.post(`/jobs/${j.id}/cancel`).then(() => onStopped?.()).catch((e) => logWarn("Stop failed", e));
    stoppedToast(0);
  }, [onStopped]);
  return { run, stop, running };
}
